// ============================================
// pages/unloading/UnloadingPaymentsLedger.tsx
// Read-only payment rollup per unloading contractor, reading from entries + bills.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Wallet, Loader2, XCircle } from 'lucide-react';
import {
  seasonsAPI, unloadingContractorsAPI, unloadingEntriesAPI, unloadingBillsAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

interface Row {
  contractor_id: number;
  firm_name: string;
  pan_number?: string;
  total_entries: number;
  total_bills: number;
  total_bags: number;
  amount_claimed: number;
  amount_verified: number;
  tds_amount: number;
  payment_released: number;
  balance: number;
  settlement_pct: number;
}

export const UnloadingPaymentsLedger: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [contractors, setContractors] = useState<any[]>([]);
  const [entries, setEntries] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([seasonsAPI.list(), unloadingContractorsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setContractors(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [e, b] = await Promise.all([
          unloadingEntriesAPI.list(selectedSeasonId),
          unloadingBillsAPI.list(selectedSeasonId),
        ]);
        setEntries(Array.isArray(e.data) ? e.data : []);
        setBills(Array.isArray(b.data) ? b.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const rows = useMemo((): Row[] => {
    const map: Record<number, Row> = {};
    for (const c of contractors) {
      map[c.id] = {
        contractor_id: c.id,
        firm_name: c.firm_name,
        pan_number: c.pan_number,
        total_entries: 0, total_bills: 0, total_bags: 0,
        amount_claimed: 0, amount_verified: 0, tds_amount: 0,
        payment_released: 0, balance: 0, settlement_pct: 0,
      };
    }
    // Prefer bills (they aggregate entries).
    for (const b of bills) {
      const r = map[b.contractor_id];
      if (!r) continue;
      r.total_bills++;
      r.total_entries += num(b.total_entries);
      r.total_bags += num(b.total_bags_agg) || num(b.num_bags_claimed);
      r.amount_claimed += num(b.total_amount_claimed_agg) || num(b.total_bill_claimed);
      r.amount_verified += num(b.total_amount_verified_agg) || num(b.mgr_total) || num(b.dm_total);
      r.tds_amount += num(b.tds_amount);
      r.payment_released += num(b.payment_released);
    }
    // Entries not aggregated into a bill yet
    const entryContractorsInBills = new Set(bills.map((b) => b.contractor_id));
    for (const e of entries) {
      if (entryContractorsInBills.has(e.contractor_id)) continue;
      const r = map[e.contractor_id];
      if (!r) continue;
      r.total_entries++;
      r.total_bags += num(e.bags_unloaded);
      r.amount_claimed += num(e.amount_claimed);
      r.amount_verified += num(e.amount_verified);
    }
    return Object.values(map).map((r) => ({
      ...r,
      balance: +(r.amount_verified - r.tds_amount - r.payment_released).toFixed(2),
      settlement_pct: r.amount_verified > 0 ? +((r.payment_released / r.amount_verified) * 100).toFixed(1) : 0,
    })).filter((r) => r.total_entries > 0 || r.total_bills > 0);
  }, [contractors, entries, bills]);

  const totals = useMemo(() => ({
    contractors: rows.length,
    entries: rows.reduce((s, r) => s + r.total_entries, 0),
    bills: rows.reduce((s, r) => s + r.total_bills, 0),
    claimed: rows.reduce((s, r) => s + r.amount_claimed, 0),
    verified: rows.reduce((s, r) => s + r.amount_verified, 0),
    paid: rows.reduce((s, r) => s + r.payment_released, 0),
    balance: rows.reduce((s, r) => s + r.balance, 0),
  }), [rows]);

  if (loading && rows.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Wallet className="w-7 h-7 text-blue-600" />
          Unloading Contractor Payments
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Read-only rollup per contractor for the selected season. Data merges approved bills with any un-aggregated entries.
        </p>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
        </select>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
          <XCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Contractors</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.contractors}</p>
          <p className="text-[10px] text-gray-400 mt-1">{totals.entries} entries · {totals.bills} bills</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Total Claimed</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{formatAmount(totals.claimed)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Total Paid</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{formatAmount(totals.paid)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Outstanding Balance</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{formatAmount(totals.balance)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contractor</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PAN</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Entries</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bills</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bags</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Claimed</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Verified</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">TDS</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Paid</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Balance</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">% Settled</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-gray-400">
                  No unloading activity for this season yet.
                </td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.contractor_id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-xs text-gray-500">{i + 1}</td>
                    <td className="px-3 py-3 text-xs font-medium text-gray-900">{r.firm_name}</td>
                    <td className="px-3 py-3 text-xs font-mono text-gray-600">{r.pan_number || '--'}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-700">{r.total_entries}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-700">{r.total_bills}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-700">{r.total_bags.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-xs text-right text-blue-600">{formatAmount(r.amount_claimed)}</td>
                    <td className="px-3 py-3 text-xs text-right font-medium text-gray-900">{formatAmount(r.amount_verified)}</td>
                    <td className="px-3 py-3 text-xs text-right text-red-600">{formatAmount(r.tds_amount)}</td>
                    <td className="px-3 py-3 text-xs text-right text-green-700">{formatAmount(r.payment_released)}</td>
                    <td className="px-3 py-3 text-xs text-right font-medium text-orange-700">{formatAmount(r.balance)}</td>
                    <td className="px-3 py-3 text-xs text-right text-gray-700">{r.settlement_pct}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UnloadingPaymentsLedger;
